import React from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  AppBar,
  Button,
  Grid,
  Hidden,
  IconButton,
  Toolbar,
  Typography
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import PostAddIcon from '@material-ui/icons/PostAdd';
import { APP_PATH_JOBS, MAIN_APP_NAME } from '../../../../config';
import NotificationCenter from '../../../../components/NotificationCenter';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.section
        : theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
  },
  flexGrow: {
    flexGrow: 1
  },
  signOutButton: {
    marginLeft: theme.spacing(1)
  },
  appNameLink: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center'
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '0.5px',
    fontSize: '1.5rem',
    position: 'relative'
  },
  topBarBtn: {},
  topPrimaryBarBtnIcon: {
    marginRight: theme.spacing(1)
  },
  topPrimaryBarBtn: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    fontSize: '16px',
    fontWeight: 700,
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.spacing(2.5),
    background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.light} 90%)`,
    color: theme.palette.common.white,
    boxShadow: `0 3px 12px rgba(0, 0, 0, 0.15)`,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px rgba(0, 0, 0, 0.2)`,
      background: `linear-gradient(45deg, ${theme.palette.secondary.dark} 30%, ${theme.palette.secondary.main} 90%)`
    }
  },
  topToolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0
  },
  menuButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary
  },
  mobileActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  mobileLogo: {
    height: 40,
    width: 40,
    display: 'block',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  mobileLogoLink: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
    color: theme.palette.text.secondary
  }
}));

const TopBar = (props) => {
  const { onSidebarOpen, onClickPlus, user } = props;
  const classes = useStyles();
  const history = useHistory();

  // Check if user is a candidate
  const isCandidate = user?.roles?.includes('ROLE_CANDIDATE');

  // Define CTA button props based on user role
  const ctaButton = isCandidate
    ? {
        text: '',
        startIcon: <SearchIcon />,
        onClick: () => history.push(APP_PATH_JOBS)
      }
    : {
        text: 'Post Your Job',
        startIcon: <PostAddIcon />,
        onClick: () => onClickPlus(props)
      };

  return (
    <AppBar className={clsx(classes.root)}>
      <Toolbar>
        <Hidden mdDown>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center">
            <Grid item>
              <RouterLink to="/" className={classes.appNameLink}>
                <Typography className={classes.title} variant="h3" align="left">
                  {MAIN_APP_NAME}
                </Typography>
              </RouterLink>
            </Grid>
          </Grid>
          <div className={classes.flexGrow} />
          <div className={classes.topToolbarActions}>
            {!isCandidate && (
              <Button
                variant="contained"
                size="large"
                disableElevation
                className={classes.topPrimaryBarBtn}
                startIcon={ctaButton.startIcon}
                onClick={ctaButton.onClick}>
                {ctaButton.text}
              </Button>
            )}
            {isCandidate && (
              <IconButton onClick={ctaButton.onClick} aria-label="search-jobs">
                <SearchIcon />
              </IconButton>
            )}
            <NotificationCenter
              userId={user?.userId || user?.id}
              enableWebSocket={true}
            />
          </div>
        </Hidden>
        <Hidden lgUp>
          <IconButton
            className={classes.menuButton}
            onClick={onSidebarOpen}
            aria-label="Open navigation menu">
            <MenuIcon />
          </IconButton>
          <RouterLink to="/" className={classes.mobileLogoLink}>
            <img
              src="/images/logos/logo.png"
              alt={MAIN_APP_NAME}
              className={classes.mobileLogo}
            />
          </RouterLink>
          <div className={classes.mobileActions}>
            <IconButton onClick={ctaButton.onClick} aria-label="search-jobs">
              <SearchIcon />
            </IconButton>
            <NotificationCenter
              userId={user?.userId || user?.id}
              enableWebSocket={true}
            />
          </div>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  onClickPlus: PropTypes.func,
  onSidebarOpen: PropTypes.func,
  user: PropTypes.object
};

export default TopBar;
